import React, { useEffect, useState, useRef } from "react";
import { useDispatch } from "react-redux";
import InfiniteScroll from "react-infinite-scroll-component";
import API_MIDDLEWARE from "../../redux/api/middleware";
import { GET_APPLIED_JOBS, GET_RECOMMENDED_JOBS } from "../../redux/api/config";
import { setErrorMessage } from "../../redux/actions/appActions";
import { APP_CUSTOM_ERROR } from "../../constants/AppVarConstants";
import { Box, CircularProgress, Grid, Stack, Chip } from "@mui/material";
import moment from "moment";
import { ROUTE_JOB_DETAILS } from "../../constants/routes";
import { useNavigate } from "react-router-dom";
import type { HighlightSection } from "../../utils/notificationsApi";

type JobApplicationsProps = {
  highlightJobId?: string;
  highlightSection?: HighlightSection;
};

function pinJobAtIndex0(records: any[], jobId: string): any[] {
  if (!records?.length) return records;
  const idx = records.findIndex((j) => String(j?.jobId) === String(jobId));
  if (idx <= 0) return records;
  const matched = records[idx];
  return [matched, ...records.slice(0, idx), ...records.slice(idx + 1)];
}

function ensureJobAtIndex0(records: any[], jobId: string | undefined, label: string): any[] {
  if (!jobId || !records?.length) return records;
  if (String(records[0]?.jobId) === String(jobId)) return records;
  console.warn(`[notifications] ${label}: expected job ${jobId} at index 0, applying client fallback`);
  return pinJobAtIndex0(records, jobId);
}

const JobApplications: React.FC<JobApplicationsProps> = ({
  highlightJobId,
  highlightSection,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [hasMore, setHasMore] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [recommendedJobs, setRecommendedJobs] = useState<any[]>([]);
  const [loadingRecommended, setLoadingRecommended] = useState(false);
  const [totalAppliedJobs, setTotalAppliedJobs] = useState(0);
  const [totalRecommendedJobs, setTotalRecommendedJobs] = useState(0);
  const [recommendedPage, setRecommendedPage] = useState(1);
  const [hasMoreRecommended, setHasMoreRecommended] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const jobDetailsNavState = highlightJobId
    ? { fromBrowseJobs: true }
    : { fromJobApplications: true };

  const fetchJobs = (flag: boolean, resetOffset: boolean, pageOverride?: number) => {
    if (!flag) {
      setLoading(true);
    }
    const pageNum = pageOverride ?? currentPage;
    const payload: Record<string, unknown> = {
      page: pageNum,
      fetchSize: 10,
    };
    if (resetOffset && pageNum === 1 && highlightSection === "applied" && highlightJobId) {
      payload.jobId = highlightJobId;
    }
    API_MIDDLEWARE(GET_APPLIED_JOBS, "POST", { "Content-Type": "application/json" }, payload)
      .then((res: any) => {
        const { records, hasNext, page, totalRecords } = res?.result || {};
        let jobs: any = resetOffset ? [...(records || [])] : [...data, ...(records || [])];
        if (resetOffset && pageNum === 1 && highlightSection === "applied" && highlightJobId) {
          jobs = ensureJobAtIndex0(jobs, highlightJobId, "applied jobs");
        }
        setData(jobs);
        setCurrentPage(page + 1);
        setHasMore(hasNext);
        setTotalAppliedJobs(totalRecords || 0);
        setLoading(false);
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        setLoading(false);
      });
  };

  const fetchRecommendedJobs = (flag: boolean, resetOffset: boolean, pageOverride?: number) => {
    setLoadingRecommended(true);
    const pageNum = pageOverride ?? recommendedPage;
    const payload: Record<string, unknown> = {
      page: pageNum,
      fetchSize: 10,
    };
    if (resetOffset && pageNum === 1 && highlightSection === "recommended" && highlightJobId) {
      payload.jobId = highlightJobId;
    }
    API_MIDDLEWARE(GET_RECOMMENDED_JOBS, "POST", { "Content-Type": "application/json" }, payload)
      .then((res: any) => {
        const { records, totalRecords, hasNext, page } = res?.result || {};
        let jobs: any = resetOffset ? [...(records || [])] : [...recommendedJobs, ...(records || [])];
        if (resetOffset && pageNum === 1 && highlightSection === "recommended" && highlightJobId) {
          jobs = ensureJobAtIndex0(jobs, highlightJobId, "recommended jobs");
        }
        setRecommendedJobs(jobs);
        setTotalRecommendedJobs(totalRecords || 0);
        setRecommendedPage(page + 1);
        setHasMoreRecommended(hasNext);
        setLoadingRecommended(false);
      })
      .catch((err: any) => {
        dispatch(setErrorMessage(err.errorMsg ?? APP_CUSTOM_ERROR));
        setLoadingRecommended(false);
      });
  };

  useEffect(() => {
    fetchJobs(false, true, 1);
    fetchRecommendedJobs(false, true, 1);
  }, [highlightJobId, highlightSection]);

  useEffect(() => {
    if (!highlightJobId) return;
    const timer = window.setTimeout(() => {
      const el = document.getElementById(`job-card-${highlightJobId}`);
      el?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [highlightJobId, recommendedJobs, data, loading, loadingRecommended]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current || loadingRecommended || !hasMoreRecommended) return;

      const container = scrollContainerRef.current;
      const { scrollLeft, scrollWidth, clientWidth } = container;
      
      // Check if scrolled near the end (within 100px)
      if (scrollLeft + clientWidth >= scrollWidth - 100) {
        fetchRecommendedJobs(true, false);
      }
    };

    const container = scrollContainerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [loadingRecommended, hasMoreRecommended, recommendedJobs]);

  return (
    <Box>
      {/* Recommended Jobs Section - At Top */}
      {recommendedJobs && recommendedJobs.length > 0 && (
        <Box 
          sx={{ 
            backgroundColor: "#FFF2EE", 
            padding: "20px", 
            mb: 4
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Box>
              <p
                style={{
                  color: "#222222",
                  fontSize: "16px",
                  fontWeight: "500",
                  margin: 0,
                }}
              >
                Recommended ({totalRecommendedJobs})
              </p>
            </Box>
          </Stack>
          <Box sx={{ mb: 2 }}>
            <p style={{ color: "#000", fontWeight: "300", fontSize: "12px", margin: 0 }}>
              Seevi recommends jobs where your profile matches the company job post
            </p>
          </Box>
          
          {/* Horizontal Scroll Container */}
          <Box
            ref={scrollContainerRef}
            sx={{
              display: "flex",
              gap: 2,
              overflowX: "auto",
              overflowY: "hidden",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              alignItems: "stretch",
            }}
          >
            {recommendedJobs.map((el: any, idx: number) => (
              <Box
                key={el?.jobId ?? idx}
                id={el?.jobId ? `job-card-${el.jobId}` : undefined}
                sx={{
                  minWidth: { xs: "calc(100% - 100px)", sm: "calc(55% - 16px)", md: "calc(45% - 16px)", lg: "calc(35% - 16px)" },
                  maxWidth: { xs: "calc(100% - 100px)", sm: "calc(55% - 16px)", md: "calc(45% - 16px)", lg: "calc(35% - 16px)" },
                  border:
                    highlightJobId && String(el?.jobId) === String(highlightJobId)
                      ? "2px solid #756DE9"
                      : "1px solid #E6E4FE",
                  borderRadius: "10px",
                  padding: "16px",
                  backgroundColor: "#ffffff",
                  flexShrink: 0,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                {/* Header with logo and title */}
                <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
                  <img
                    src={el?.companyDetails?.logo}
                    alt=""
                    style={{ width: "50px", height: "50px", borderRadius: "8px", objectFit: "contain" }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <h4 style={{ fontSize: "14px", fontWeight: "600", margin: 0, color: "#000" }}>
                      {el?.title}
                    </h4>
                    <p style={{ color: "#ADADAD", fontSize: "12px", margin: "4px 0 0 0" }}>
                      {el?.companyDetails?.name}
                    </p>
                  </Box>
                </Stack>

                {/* Details - 2 per row, 3 rows total */}
                <Box sx={{ mb: 2, flex: 1 }}>
                  {/* Row 1: Location and CTC */}
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      spacing={0.5}
                      sx={{ 
                        flex: 1,
                        backgroundColor: "rgb(230, 228, 254, 0.4)",
                        borderRadius: "5px",
                        padding: "6px 8px"
                      }}
                    >
                      <img src="/images/saved-jobs/icon-1.png" alt="" width="14px" />
                      <Box>
                        <h6 style={{ fontSize: "10px", margin: 0 }}>
                          {el?.location?.join(", ")}
                        </h6>
                      </Box>
                    </Stack>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      spacing={0.5}
                      sx={{ 
                        flex: 1,
                        backgroundColor: "rgb(230, 228, 254, 0.4)",
                        borderRadius: "5px",
                        padding: "6px 8px"
                      }}
                    >
                      <img src="/images/complete-profile/CTC.svg" alt="" width="14px" />
                      <Box>
                        <h6 style={{ fontSize: "10px", margin: 0 }}>
                          {el?.minCtc} - {el?.maxCtc}
                        </h6>
                      </Box>
                    </Stack>
                  </Stack>
                  
                  {/* Row 2: Experience and Industry */}
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      spacing={0.5}
                      sx={{ 
                        flex: 1,
                        backgroundColor: "rgb(230, 228, 254, 0.4)",
                        borderRadius: "5px",
                        padding: "6px 8px"
                      }}
                    >
                      <img src="/images/complete-profile/Experience.svg" alt="" width="14px" />
                      <Box>
                        <h6 style={{ fontSize: "10px", margin: 0 }}>
                          {el?.minExperience} - {el?.maxExperience}
                        </h6>
                      </Box>
                    </Stack>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      spacing={0.5}
                      sx={{ 
                        flex: 1,
                        backgroundColor: "rgb(230, 228, 254, 0.4)",
                        borderRadius: "5px",
                        padding: "6px 8px"
                      }}
                    >
                      <img src="/images/complete-profile/Industry.svg" alt="" width="14px" />
                      <Box>
                        <h6 style={{ fontSize: "10px", margin: 0 }}>
                          {el?.industryName}
                        </h6>
                      </Box>
                    </Stack>
                  </Stack>

                  {/* Row 3: Job Type and Work Mode */}
                  <Stack direction="row" spacing={1}>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      spacing={0.5}
                      sx={{ 
                        flex: 1,
                        backgroundColor: "rgb(230, 228, 254, 0.4)",
                        borderRadius: "5px",
                        padding: "6px 8px"
                      }}
                    >
                      <img src="/images/complete-profile/Job Type.svg" alt="" width="14px" />
                      <Box>
                        <h6 style={{ fontSize: "10px", margin: 0 }}>
                          {el?.jobType?.join(", ")}
                        </h6>
                      </Box>
                    </Stack>
                    <Stack 
                      direction="row" 
                      alignItems="center" 
                      spacing={0.5}
                      sx={{ 
                        flex: 1,
                        backgroundColor: "rgb(230, 228, 254, 0.4)",
                        borderRadius: "5px",
                        padding: "6px 8px"
                      }}
                    >
                      <img src="/images/complete-profile/Job Mode.svg" alt="" width="14px" />
                      <Box>
                        <h6 style={{ fontSize: "10px", margin: 0 }}>
                          {el?.workMode?.join(", ")}
                        </h6>
                      </Box>
                    </Stack>
                  </Stack>
                </Box>

                {/* View button */}
                <Box
                  onClick={() => navigate(`${ROUTE_JOB_DETAILS}/${el?.jobId}`, { state: jobDetailsNavState })}
                  sx={{
                    width: "100%",
                    backgroundColor: "#756DE9",
                    borderRadius: "25px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    transition: "background-color 0.3s ease",
                    "&:hover": {
                      backgroundColor: "#6159d1",
                    },
                  }}
                >
                  <p style={{ color: "#fff" , padding: "10px", fontSize: "13px", fontWeight: "500", margin: 0 }}>
                    View job opening
                  </p>
                  <img
                    src="/images/arrow-right-line.png"
                    width="14px"
                    alt=""
                    style={{ marginLeft: "8px", filter: "brightness(0) invert(1)" }}
                  />
                </Box>
              </Box>
            ))}
            {/* Loading indicator at the end */}
            {loadingRecommended && (
            <Box
                sx={{
                  minWidth: "100px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <CircularProgress size={30} />
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* Applied Jobs Section */}
      {data?.length ? (
        <Stack direction="row" justifyContent="space-between" sx={{ my: "10px" }}>
          <Stack direction="row" justifyContent="space-evenly" alignItems="center">
            <Box>
              <p
                style={{
                  color: "#222222",
                  fontSize: "16px",
                  fontWeight: "500",
                }}
              >
                Applied ({totalAppliedJobs})
              </p>
            </Box>
          </Stack>
        </Stack>
      ) : null}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
          <CircularProgress />
        </Box>
      ) : (
        <InfiniteScroll
          dataLength={data.length}
          next={() => fetchJobs(true, false)}
          hasMore={hasMore}
          loader={<h4>Loading...</h4>}
          endMessage={
            <Box sx={{ textAlign: "center", mb: 3, mt: 1 }}>
              <b></b>
            </Box>
          }
        >
          {data?.map((el: any, idx: number) => {
            const isHighlighted =
              highlightJobId && String(el?.jobId) === String(highlightJobId);
            return (
              <Box
                key={el?.jobId ?? idx}
                id={el?.jobId ? `job-card-${el.jobId}` : undefined}
                sx={{
                  flexGrow: 1,
                  paddingBottom: "20px",
                  border: isHighlighted ? "2px solid #756DE9" : "1px solid #b6b2ea",
                  borderRadius: "10px",
                  padding: "10px",
                }}
                className="mt-1 mb-3"
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box
                      style={{
                        background: `#ffffff`,
                        borderRadius: "20px",
                      }}
                      className="mt-3"
                    >
                      <Stack direction="row" justifyContent="space-between">
                        <Box>
                          <Stack direction="row">
                            <img
                              src={el?.companyDetails?.logo}
                              alt=""
                              className="img-fluid"
                              style={{ width: "100px" }}
                            />
                            <Box className="ms-3">
                              <h4 className="text-dark">{el?.title}</h4>
                              <p style={{ color: "#ADADAD" }}>{el?.companyDetails?.name}</p>
                              
                              {/* Application Status - only show SHORTLIST and DECLINED */}
                              {el?.applicationStatus && (
                                <Box sx={{ mt: 0.5 }}>
                                  {el.applicationStatus === "SHORTLIST" && (
                                    <Chip
                                      label="SHORTLISTED"
                                      size="small"
                                      sx={{
                                        bgcolor: "#52C6AE33",
                                        color: "#41AE97",
                                        fontWeight: 600,
                                        fontSize: "0.80rem",
                                        height: "20px",
                                      }}
                                    />
                                  )}
                                  {el.applicationStatus === "DECLINED" && (
                                    <Chip
                                      label="DECLINED"
                                      size="small"
                                      sx={{
                                        bgcolor: "#D1412A17",
                                        color: "#D1412A",
                                        fontWeight: 600,
                                        fontSize: "0.80rem",
                                        height: "20px",
                                      }}
                                    />
                                  )}
                                </Box>
                              )}
                            </Box>
                          </Stack>
                        </Box>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" className="mt-3">
                        <Stack
                          direction="row"
                          justifyContent="space-evenly"
                          alignItems="center"
                          padding="8px"
                          width="28%"
                          borderRadius="5px"
                          sx={{ backgroundColor: "rgb(230, 228, 254, 0.4)" }}
                        >
                          <img src="/images/saved-jobs/icon-1.png" alt="" width="16px" />
                          <Box>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginLeft: "4px",
                              }}
                            >
                              {el?.location?.join(", ")}
                            </h6>
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          justifyContent="space-evenly"
                          alignItems="center"
                          padding="8px"
                          width="28%"
                          borderRadius="5px"
                          sx={{ backgroundColor: "rgb(230, 228, 254, 0.4)" }}
                        >
                          <img src="/images/complete-profile/CTC.svg" alt="" width="16px" />
                          <Box>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginLeft: "4px",
                              }}
                            >
                              {el?.minCtc} - {el?.maxCtc}
                            </h6>
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          justifyContent="space-evenly"
                          alignItems="center"
                          padding="8px"
                          width="28%"
                          borderRadius="5px"
                          sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                        >
                          <img src="/images/complete-profile/Experience.svg" alt="" width="16px" />
                          <Box>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginLeft: "4px",
                              }}
                            >
                              {el?.minExperience} - {el?.maxExperience}
                            </h6>
                          </Box>
                        </Stack>
                      </Stack>

                      <Stack direction="row" justifyContent="space-between" className="mt-4">
                        <Stack
                          direction="row"
                          justifyContent="space-evenly"
                          alignItems="left"
                          padding="8px"
                          width="28%"
                          borderRadius="5px"
                          sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                        >
                          <img src="/images/complete-profile/Industry.svg" alt="" width="16px" />
                          <Box>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginLeft: "4px",
                              }}
                            >
                              {el?.industryName}
                            </h6>
                          </Box>
                        </Stack>

                        <Stack
                          direction="row"
                          justifyContent="space-evenly"
                          alignItems="center"
                          padding="8px"
                          width="28%"
                          borderRadius="5px"
                          sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                        >
                          <img src="/images/complete-profile/Job Type.svg" alt="" width="16px" />
                          <Box>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginLeft: "4px",
                              }}
                            >
                              {el?.jobType?.join(", ")}
                            </h6>
                          </Box>
                        </Stack>
                        <Stack
                          direction="row"
                          justifyContent="space-evenly"
                          alignItems="center"
                          padding="8px"
                          width="28%"
                          borderRadius="5px"
                          sx={{ backgroundColor: "rgb(230, 228, 254, 0.41)" }}
                        >
                          <img src="/images/complete-profile/Job Mode.svg" alt="" width="16px" />
                          <Box>
                            <h6
                              style={{
                                fontSize: "14px",
                                fontWeight: "bold",
                                marginLeft: "4px",
                              }}
                            >
                              {el?.workMode?.join(", ")}
                            </h6>
                          </Box>
                        </Stack>
                      </Stack>

                      <Stack direction="row" alignItems="end" className="mt-1" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" className="mt-3">
                          <img src="/images/check-double-line.png" width="14px" alt="" className="me-2" />
                          <p
                            style={{
                              color: "#777777",
                              fontSize: "14x",
                              fontWeight: "500",
                            }}
                          >
                            Applied {moment(el?.createdAt).fromNow()}
                          </p>
                        </Stack>
                        <Stack
                          onClick={() => navigate(`${ROUTE_JOB_DETAILS}/${el?.jobId}`, { state: jobDetailsNavState })}
                          direction="row"
                          alignItems="center"
                          className="mt-3"
                        >
                          <p className="me-2">View</p>
                          <img src="/images/Vector.png" width="14px" alt="" className="me-2" />
                        </Stack>
                      </Stack>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            );
          })}
        </InfiniteScroll>
      )}
    </Box>
  );
};

export default JobApplications;
